import { Connect } from '@genesislcap/foundation-comms';
import { isOrderEligibleForReconfirm, getQuantityCellValidationState, isReconfirmResetTarget } from '../deal-detail/deal-reconfirm.utils';
import { MaturityOrder, MuniOrderType, OrderStatusEnum, PriorityTypeEnum, RetailTypeEnum, successOrderStatuses } from './deal-series.types';
import {
  allowOnlyWholePositiveNumbers,
  buildAcceptedOrderBaseline,
  canSetZeroCancelOrderAmount,
  getOrderAmountCap,
  getSplitConditionsSelectionFingerprint,
  hasOrderAmountApplied,
  hasOrderConditions,
  hasSubmittedAmountOnRecord,
  isDraftOrder,
  isFullParOrderAmountApplied,
  isOrderInTransit,
  formatOrderQuantityForCell,
  normalizeNumericValueForDisplay,
  normalizeNumericValueForStorage,
  orderAmountFormatter,
  parseNumberAmount,
} from './deal-series.util';
import { SplitOrderRow } from './split-order-modal/split-order-modal.types';
import { getSplitRowValidationOrder } from './deal-series.quantity-validation';

const PARENT_BOND_FIELDS: (keyof MaturityOrder)[] = [
  'MATURITY_ID',
  'SERIES_ID',
  'SERIES_CODE',
  'DEAL_ID',
  'MATURITY_DATE',
  'MATURITY_AMOUNT',
  'SECURITY_TYPE',
  'MATURITY_PRESENT_VALUE',
  'MULTIPLES',
  'COUPON',
  'YIELD',
  'PRICE',
  'SPREAD',
  'TAX_TREASURY_SPREAD',
  'PUT_DATE',
  'CALL_DATE_OVERRIDE',
  'COUPON_CHANGED',
  'YIELD_CHANGED',
  'PRICE_CHANGED',
  'MATURITY_AMOUNT_CHANGED',
];

const SPLIT_CONDITION_FIELDS: (keyof MaturityOrder)[] = [
  'ORDER_TYPE',
  'RETAIL_TYPE',
  'PRIORITY_TYPE',
  'MUNI_AON',
  'MUNI_STRIP',
  'MINIMUM_ORDER_AMOUNT',
  'MAXIMUM_ORDER_AMOUNT',
  'COMMENT',
  'ZIP_CODE',
  'FLEX_MATURITY',
  'FLEX_COUPON',
  'ORDER_AMOUNT_INCREMENT',
];

function copyDefinedFields<T extends object, K extends keyof T>(target: T, source: T, fields: readonly K[]): void {
  for (const field of fields) {
    const value = source[field];
    if (value !== undefined) {
      target[field] = value;
    }
  }
}

function copyFields<T extends object, K extends keyof T>(target: T, source: T, fields: readonly K[]): void {
  for (const field of fields) {
    target[field] = source[field];
  }
}

function clearSplitConditionFields(order: MaturityOrder): void {
  order.ORDER_TYPE = MuniOrderType.OTHER;
  order.RETAIL_TYPE = RetailTypeEnum.NONE;
  order.PRIORITY_TYPE = PriorityTypeEnum.NONE;
  order.MUNI_AON = false;
  order.MUNI_STRIP = false;
  order.MINIMUM_ORDER_AMOUNT = undefined;
  order.MAXIMUM_ORDER_AMOUNT = undefined;
  order.COMMENT = undefined;
  order.ZIP_CODE = undefined;
  order.FLEX_MATURITY = undefined;
  order.FLEX_COUPON = undefined;
  order.ORDER_AMOUNT_INCREMENT = undefined;
}

export type CreateEmptySplitOrderOptions = {
  /** When true, copy condition fields from the source order (first split of an existing order). */
  inheritConditions?: boolean;
  /**
   * When true, preserve submitted-track status fields from the parent lone order on the first
   * migrated split line (before Save Split creates the anchor/child relationship on the server).
   */
  inheritSubmittedTrack?: boolean;
};

/** Blank split line — bond fields from parent, no persisted identity; conditions cleared unless inherited. */
export function createEmptySplitOrder(
  sourceOrder: MaturityOrder,
  options?: CreateEmptySplitOrderOptions,
): MaturityOrder {
  const inheritSubmittedTrack = options?.inheritSubmittedTrack === true;
  const order: MaturityOrder = {
    ...sourceOrder,
    ORDER_ID: '',
    QUANTITY: '',
    IS_SPLIT_MATURITY: false,
    HAS_SPLIT: false,
    IS_SPLIT: true,
    ORDER_STATUS: inheritSubmittedTrack
      ? sourceOrder.ORDER_STATUS ?? OrderStatusEnum.DRAFT
      : OrderStatusEnum.DRAFT,
    SUBMITTED_QUANTITY: inheritSubmittedTrack ? sourceOrder.SUBMITTED_QUANTITY : undefined,
    LINE_NO: undefined,
    SPLIT_LINE_NO: undefined,
    SPLIT_CONDITIONS_VARIOUS: false,
    IS_AUTO_RESET_ORDER_AMOUNT: false,
    IS_ZERO_CANCEL_INTENT: false,
    IS_CONFIRMED: inheritSubmittedTrack ? sourceOrder.IS_CONFIRMED : undefined,
    RECONFIRM_ELIGIBLE: inheritSubmittedTrack ? sourceOrder.RECONFIRM_ELIGIBLE : undefined,
    ERROR_MESSAGE: inheritSubmittedTrack ? sourceOrder.ERROR_MESSAGE : undefined,
  };
  if (!options?.inheritConditions) {
    clearSplitConditionFields(order);
  }
  return order;
}

/** New split line: bond fields from parent maturity; conditions cleared (not copied from siblings). */
export function createNewSplitRowOrder(sourceOrder: MaturityOrder): MaturityOrder {
  return createEmptySplitOrder(sourceOrder, { inheritConditions: false });
}

function createSourceSplitOrderRow(sourceOrder: MaturityOrder): SplitOrderRow {
  return {
    rowId: 'source',
    rowType: 'source',
    splitAmount: '',
    order: { ...sourceOrder },
  };
}

function createBlankSplitRow(sourceOrder: MaturityOrder, rowId: string): SplitOrderRow {
  return {
    rowId,
    rowType: 'split',
    splitAmount: '',
    order: createNewSplitRowOrder(sourceOrder),
  };
}

/** Default modal rows when opening split with no persisted child lines on the server. */
export function buildInitialSplitRowsForOpen(
  sourceOrder: MaturityOrder,
  nextSplitRowId: { value: number },
): SplitOrderRow[] {
  const sourceRow = createSourceSplitOrderRow(sourceOrder);
  const nextRowId = (): string => {
    nextSplitRowId.value += 1;
    return `split-${nextSplitRowId.value}`;
  };

  const hasPersistedOrder = !!String(sourceOrder.ORDER_ID ?? '').trim();
  const inheritedAmount = formatOrderQuantityForCell(sourceOrder);
  const shouldSeedFirst =
    hasPersistedOrder &&
    (hasOrderAmountApplied(sourceOrder) || inheritedAmount === '0');

  if (shouldSeedFirst) {
    const isZeroCancel = sourceOrder.IS_ZERO_CANCEL_INTENT === true;
    const inheritedOrder = createEmptySplitOrder(sourceOrder, {
      inheritConditions: true,
      inheritSubmittedTrack: true,
    });
    const acceptedOrderBaseline = buildAcceptedOrderBaseline(inheritedOrder) ?? undefined;
    const inheritedQuantity =
      normalizeNumericValueForStorage(inheritedAmount, inheritedOrder) ?? '';
    const childOrder: MaturityOrder = isZeroCancel
      ? {
          ...(acceptedOrderBaseline ?? inheritedOrder),
          QUANTITY: '0',
          IS_ZERO_CANCEL_INTENT: true,
          ORDER_STATUS: OrderStatusEnum.SUBMITTED_AND_DRAFT,
        }
      : acceptedOrderBaseline
        ? { ...acceptedOrderBaseline }
        : { ...inheritedOrder, QUANTITY: inheritedQuantity };

    return [
      sourceRow,
      {
        rowId: nextRowId(),
        rowType: 'split',
        splitAmount: inheritedAmount,
        acceptedOrderBaseline: acceptedOrderBaseline ?? undefined,
        order: childOrder,
      },
      createBlankSplitRow(sourceOrder, nextRowId()),
    ];
  }

  return [
    sourceRow,
    createBlankSplitRow(sourceOrder, nextRowId()),
    createBlankSplitRow(sourceOrder, nextRowId()),
  ];
}

/**
 * Whether the split modal Order Status column should render for this row.
 * Unsaved blank lines stay hidden; migrated parent amount rows show inherited status before Save Split.
 */
export function shouldShowSplitRowOrderStatus(row: SplitOrderRow | undefined): boolean {
  if (!row || row.rowType === 'source') {
    return true;
  }
  if (String(row.order.ORDER_ID ?? '').trim() !== '') {
    return true;
  }
  const hasMigratedAmount = String(row.splitAmount ?? '').trim() !== '';
  return hasMigratedAmount && row.order.ORDER_STATUS != null;
}

/** Parent status when opening split from a submitted-track source with zero/empty amount and blank child lines. */
function resolveSplitParentStatusFromZeroAmountSource(
  sourceOrder: MaturityOrder,
): OrderStatusEnum | undefined {
  if (sourceOrder.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT) {
    return OrderStatusEnum.SUBMITTED_AND_DRAFT;
  }
  if (hasSubmittedAmountOnRecord(sourceOrder) && !hasOrderAmountApplied(sourceOrder)) {
    return OrderStatusEnum.SUBMITTED_AND_DRAFT;
  }
  return undefined;
}

const SPLIT_BASELINE_COMPARE_FIELDS: (keyof MaturityOrder)[] = [
  'ORDER_STATUS',
  'SUBMITTED_QUANTITY',
  'IS_CONFIRMED',
  'RECONFIRM_ELIGIBLE',
  'ERROR_MESSAGE',
  'IS_AUTO_RESET_ORDER_AMOUNT',
];

/** True when a persisted server child differs from the split line's accepted baseline snapshot. */
export function persistedSplitRowDiffersFromAcceptedBaseline(
  sourceOrder: MaturityOrder,
  serverChild: MaturityOrder,
  acceptedOrderBaseline: MaturityOrder | undefined,
): boolean {
  if (!acceptedOrderBaseline) {
    return false;
  }
  const merged = mergeChildWithGridParent(sourceOrder, serverChild);
  return splitRowHasEditsFromAcceptedBaseline({
    rowId: '',
    rowType: 'split',
    splitAmount: orderAmountFormatter(serverChild.QUANTITY),
    order: merged,
    acceptedOrderBaseline,
  });
}

/** True when a split line's local edits diverge from the accepted snapshot captured at open. */
export function splitRowHasEditsFromAcceptedBaseline(row: SplitOrderRow): boolean {
  const baseline = row.acceptedOrderBaseline;
  if (!baseline) {
    return false;
  }
  const baselineAmount = normalizeNumericValueForDisplay(baseline.QUANTITY, baseline);
  const baselineQty =
    normalizeNumericValueForStorage(baselineAmount, baseline) ?? '';
  const currentQty =
    normalizeNumericValueForStorage(row.splitAmount, row.order) ?? '';
  if (baselineQty !== currentQty) {
    return true;
  }
  for (const field of SPLIT_CONDITION_FIELDS) {
    if (baseline[field] !== row.order[field]) {
      return true;
    }
  }
  for (const field of SPLIT_BASELINE_COMPARE_FIELDS) {
    if (baseline[field] !== row.order[field]) {
      return true;
    }
  }
  return false;
}

/** Restore a split modal row to its immutable accepted snapshot (unsaved lines only). */
export function restoreSplitRowToAcceptedBaseline(row: SplitOrderRow): SplitOrderRow | null {
  const baseline = row.acceptedOrderBaseline;
  if (!baseline) {
    return null;
  }
  const quantity = normalizeNumericValueForStorage(baseline.QUANTITY, baseline) ?? '';
  const splitAmount = orderAmountFormatter(quantity);
  return {
    rowId: row.rowId,
    rowType: row.rowType,
    splitAmount,
    acceptedOrderBaseline: row.acceptedOrderBaseline,
    order: {
      ...baseline,
      ORDER_ID: row.order.ORDER_ID ?? '',
      QUANTITY: quantity,
      IS_ZERO_CANCEL_INTENT: false,
      IS_SPLIT: true,
      LINE_NO: row.order.LINE_NO,
      SPLIT_LINE_NO: row.order.SPLIT_LINE_NO,
      IS_SPLIT_MATURITY: row.order.IS_SPLIT_MATURITY,
      HAS_SPLIT: row.order.HAS_SPLIT,
    },
  };
}

export function mergeChildWithGridParent(parent: MaturityOrder, child: MaturityOrder): MaturityOrder {
  const merged: MaturityOrder = { ...child };
  copyDefinedFields(merged, parent, PARENT_BOND_FIELDS);
  merged.IS_SPLIT = true;
  merged.IS_SPLIT_MATURITY = false;
  merged.HAS_SPLIT = false;
  return merged;
}

/** Grid row after collapsing a single-line split back to a lone order (child promoted to LINE_NO=0). */
export function buildCollapsedLoneOrderFromSplitChild(
  parent: MaturityOrder,
  child: MaturityOrder,
): MaturityOrder {
  const merged = mergeChildWithGridParent(parent, child);
  return {
    ...merged,
    HAS_SPLIT: false,
    IS_SPLIT_MATURITY: false,
    IS_SPLIT: false,
    LINE_NO: 0,
    SPLIT_LINE_NO: undefined,
    SPLIT_CONDITIONS_VARIOUS: false,
    IS_AUTO_RESET_ORDER_AMOUNT: false,
    IS_ZERO_CANCEL_INTENT: false,
  };
}

export type SplitParentStatusFields = Pick<
  MaturityOrder,
  'ORDER_STATUS' | 'ERROR_MESSAGE' | 'IS_CONFIRMED' | 'RECONFIRM_ELIGIBLE'
>;

function getEffectiveSplitLineStatus(order: MaturityOrder): OrderStatusEnum | undefined {
  if (order.ORDER_STATUS != null) {
    return order.ORDER_STATUS;
  }
  if (String(order.ORDER_ID ?? '').trim() === '') {
    return OrderStatusEnum.DRAFT;
  }
  return undefined;
}

function buildSplitParentStatusFields(
  eligible: MaturityOrder[],
  orderStatus: OrderStatusEnum | undefined,
): SplitParentStatusFields {
  if (orderStatus == null) {
    return {
      ORDER_STATUS: undefined,
      ERROR_MESSAGE: undefined,
      IS_CONFIRMED: undefined,
      RECONFIRM_ELIGIBLE: undefined,
    };
  }

  const successStatusLines = eligible.filter(
    (order) => order.ORDER_STATUS != null && successOrderStatuses.has(order.ORDER_STATUS),
  );
  const errorLine = successStatusLines.find(
    (order) => String(order.ERROR_MESSAGE ?? '').trim() !== '',
  );
  const isConfirmed =
    successStatusLines.length > 0 &&
    successStatusLines.every((order) => order.IS_CONFIRMED === true);

  return {
    ORDER_STATUS: orderStatus,
    ERROR_MESSAGE: errorLine?.ERROR_MESSAGE,
    IS_CONFIRMED: isConfirmed ? true : undefined,
    RECONFIRM_ELIGIBLE: eligible.some((order) => order.RECONFIRM_ELIGIBLE === true)
      ? true
      : undefined,
  };
}

/** Derives parent order status fields from all eligible split child lines. */
export function deriveSplitParentStatusFromChildren(
  splitOrders: MaturityOrder[],
  sourceOrder?: MaturityOrder,
): SplitParentStatusFields {
  const eligible = splitOrders.filter(
    (order) =>
      !!String(order.ORDER_ID ?? '').trim() || hasOrderAmountApplied(order),
  );
  if (eligible.length === 0) {
    const fallback = sourceOrder
      ? resolveSplitParentStatusFromZeroAmountSource(sourceOrder)
      : undefined;
    if (fallback != null) {
      return buildSplitParentStatusFields(splitOrders, fallback);
    }
    return buildSplitParentStatusFields(eligible, undefined);
  }

  if (eligible.some((order) => isOrderInTransit(order))) {
    return buildSplitParentStatusFields(
      eligible,
      eligible.some((order) => order.ORDER_STATUS === OrderStatusEnum.PENDING_CANCELLED)
        ? OrderStatusEnum.PENDING_CANCELLED
        : OrderStatusEnum.PENDING,
    );
  }
  if (eligible.some((order) => order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_INTERNAL_FAILURE)) {
    return buildSplitParentStatusFields(eligible, OrderStatusEnum.SUBMITTED_INTERNAL_FAILURE);
  }
  if (eligible.some((order) => order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_EXTERNAL_FAILURE)) {
    return buildSplitParentStatusFields(eligible, OrderStatusEnum.SUBMITTED_EXTERNAL_FAILURE);
  }

  const statuses = eligible.map(getEffectiveSplitLineStatus);
  const definedStatuses = statuses.filter((status): status is OrderStatusEnum => status != null);
  if (definedStatuses.length === 0) {
    return buildSplitParentStatusFields(eligible, undefined);
  }

  if (definedStatuses.every((status) => successOrderStatuses.has(status))) {
    const orderStatus = definedStatuses.every((status) => status === OrderStatusEnum.ACCEPTED)
      ? OrderStatusEnum.ACCEPTED
      : OrderStatusEnum.SUBMITTED;
    return buildSplitParentStatusFields(eligible, orderStatus);
  }

  if (definedStatuses.every((status) => status === OrderStatusEnum.SUBMITTED_AND_DRAFT)) {
    return buildSplitParentStatusFields(eligible, OrderStatusEnum.SUBMITTED_AND_DRAFT);
  }

  if (definedStatuses.every((status) => status === OrderStatusEnum.DRAFT)) {
    return buildSplitParentStatusFields(eligible, undefined);
  }

  return buildSplitParentStatusFields(eligible, OrderStatusEnum.SUBMITTED_AND_DRAFT);
}

export function getSplitConditionsRollupOrder(
  sourceOrder: MaturityOrder,
  splitRows: SplitOrderRow[],
): MaturityOrder {
  const splitOrders = splitRows
    .filter((row) => row.rowType === 'split')
    .map((row) => row.order);
  const statusFields = deriveSplitParentStatusFromChildren(splitOrders, sourceOrder);
  const rollup: MaturityOrder = {
    ...sourceOrder,
    HAS_SPLIT: true,
    IS_SPLIT_MATURITY: true,
    ...statusFields,
  };

  if (splitOrders.length === 0) {
    return rollup;
  }

  const fingerprints = new Set(splitOrders.map((order) => getSplitConditionsSelectionFingerprint(order)));
  if (fingerprints.size > 1) {
    return {
      ...rollup,
      SPLIT_CONDITIONS_VARIOUS: true,
      ...statusFields,
    };
  }

  copyFields(rollup, splitOrders[0], SPLIT_CONDITION_FIELDS);
  rollup.SPLIT_CONDITIONS_VARIOUS = false;
  return {
    ...rollup,
    ...statusFields,
  };
}

function looksLikeBondOrder(record: Record<string, unknown>): boolean {
  return (
    'ORDER_ID' in record ||
    'orderId' in record ||
    'MATURITY_ID' in record ||
    'maturityId' in record
  );
}

function readLineNo(record: Record<string, unknown>): number | undefined {
  const raw = record.LINE_NO ?? record.lineNo ?? record.SPLIT_LINE_NO ?? record.splitLineNo;
  if (raw == null || raw === '') return undefined;
  return Number(raw);
}

function readHasSplit(record: Record<string, unknown>): boolean {
  return (
    record.HAS_SPLIT === true ||
    record.hasSplit === true ||
    record.IS_SPLIT_MATURITY === true ||
    record.isSplitMaturity === true
  );
}

export function normalizeBondOrderReply(record: Record<string, unknown>): MaturityOrder {
  const orderId = String(record.ORDER_ID ?? record.orderId ?? '');
  const quantity = record.QUANTITY ?? record.quantity;
  const lineNo = readLineNo(record);
  const hasSplit = readHasSplit(record);
  const quantityForStorage =
    typeof quantity === 'string' || typeof quantity === 'number' ? quantity : undefined;
  const isSplitChild = lineNo != null && lineNo > 0;
  return {
    ...(record as unknown as MaturityOrder),
    ORDER_ID: orderId,
    QUANTITY: normalizeNumericValueForStorage(quantityForStorage) ?? '',
    LINE_NO: lineNo,
    SPLIT_LINE_NO: isSplitChild ? lineNo : undefined,
    HAS_SPLIT: hasSplit,
    IS_SPLIT_MATURITY: hasSplit,
    IS_SPLIT: isSplitChild,
  };
}

function extractReplyRecords(reply: unknown): Record<string, unknown>[] {
  if (reply == null) {
    return [];
  }
  if (Array.isArray(reply)) {
    return reply.filter(
      (item): item is Record<string, unknown> => item != null && typeof item === 'object',
    );
  }
  if (typeof reply === 'object') {
    const obj = reply as Record<string, unknown>;
    if (looksLikeBondOrder(obj)) {
      return [obj];
    }
    const values = Object.values(obj);
    if (
      values.length > 0 &&
      values.every(
        (value) =>
          value != null && typeof value === 'object' && looksLikeBondOrder(value as Record<string, unknown>),
      )
    ) {
      return values as Record<string, unknown>[];
    }
  }
  return [];
}

function isRequestFailure(response: Record<string, unknown>): boolean {
  if (!('MESSAGE_TYPE' in response)) {
    return false;
  }
  const messageType = String(response.MESSAGE_TYPE ?? '');
  if (messageType === 'ERROR' || messageType === 'MSG_NACK') {
    return true;
  }
  const reply = response.REPLY ?? response.reply;
  return typeof reply === 'string' && reply.includes('MSG_NACK');
}

/** Parses a REQ_BOND_ORDER response envelope (or raw reply payload) into child orders. */
export function parseBondOrderRequestReply(raw: unknown): MaturityOrder[] {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return extractReplyRecords(raw).map((record) => normalizeBondOrderReply(record));
  }
  if (typeof raw !== 'object') {
    return [];
  }

  const response = raw as Record<string, unknown>;
  if (isRequestFailure(response)) {
    return [];
  }

  const reply = response.REPLY ?? response.reply ?? response.DETAILS;
  let records = extractReplyRecords(reply);
  if (records.length === 0 && looksLikeBondOrder(response)) {
    records = [response];
  }

  return records
    .map((record) => normalizeBondOrderReply(record))
    .sort((a, b) => (a.LINE_NO ?? a.SPLIT_LINE_NO ?? Number.MAX_SAFE_INTEGER) - (b.LINE_NO ?? b.SPLIT_LINE_NO ?? Number.MAX_SAFE_INTEGER));
}

/**
 * While split-parent transit hold is active, pin streamed anchor rows away from
 * SUBMITTED_AND_DRAFT so the grid shows PENDING (spinner) until child rollups refresh.
 */
export function coerceSplitParentDisplayStatusDuringTransitHold(
  order: MaturityOrder,
  transitHoldActive: boolean,
): MaturityOrder {
  if (
    !transitHoldActive ||
    !isSplitMaturityGridOrder(order) ||
    order.ORDER_STATUS !== OrderStatusEnum.SUBMITTED_AND_DRAFT
  ) {
    return order;
  }
  return { ...order, ORDER_STATUS: OrderStatusEnum.PENDING };
}

/** True when the grid row is a split-maturity rollup anchor (LINE_NO=0, HAS_SPLIT=true). */
export function isSplitMaturityGridOrder(order: MaturityOrder): boolean {
  const record = order as MaturityOrder & { hasSplit?: boolean; isSplitMaturity?: boolean };
  return (
    order.IS_SPLIT_MATURITY === true ||
    order.HAS_SPLIT === true ||
    record.hasSplit === true ||
    record.isSplitMaturity === true
  );
}

/** True when the row is a persisted split child (LINE_NO > 0), not the rollup anchor. */
export function isSplitChildOrder(order: MaturityOrder | null | undefined): boolean {
  if (!order) return false;
  return (order.LINE_NO != null && order.LINE_NO > 0) || order.IS_SPLIT === true;
}

export async function fetchBondOrderChildren(
  connect: Connect,
  maturityId: string | number,
): Promise<MaturityOrder[]> {
  try {
    const parsedMaturityId = Number(maturityId);
    const response = await connect.request('REQ_BOND_ORDER', {
      REQUEST: {
        MATURITY_ID: parsedMaturityId,
        maturityId: parsedMaturityId,
      },
    });
    return parseBondOrderRequestReply(response).filter((order) => (order.LINE_NO ?? 0) > 0);
  } catch {
    return [];
  }
}

export function resolveSplitRowOrderId(
  sourceOrder: MaturityOrder,
  row: SplitOrderRow,
  index: number,
): string {
  const anchorOrderId = isSplitMaturityGridOrder(sourceOrder) ? sourceOrder.ORDER_ID : '';
  const rowOrderId = (row.order.ORDER_ID ?? '').trim();
  if (rowOrderId && rowOrderId !== anchorOrderId) {
    return rowOrderId;
  }
  if (index === 0 && !isSplitMaturityGridOrder(sourceOrder) && sourceOrder.ORDER_ID) {
    return sourceOrder.ORDER_ID;
  }
  return '';
}

/** Resolves persisted quantity for a split row (matches order-amount cell display rules). */
export function resolveSplitRowQuantity(row: SplitOrderRow): string {
  if (row.rowType !== 'split') return '';
  const splitAmount = String(row.splitAmount ?? '').trim();
  if (splitAmount !== '') {
    return allowOnlyWholePositiveNumbers(splitAmount);
  }
  const orderQty = allowOnlyWholePositiveNumbers(String(row.order.QUANTITY ?? '').trim());
  if (
    orderQty === '0' &&
    (row.order.IS_ZERO_CANCEL_INTENT === true || canSetZeroCancelOrderAmount(row.order))
  ) {
    return '0';
  }
  const orderWithAmount: MaturityOrder = { ...row.order, QUANTITY: row.splitAmount };
  if (!isFullParOrderAmountApplied(orderWithAmount)) {
    return '';
  }
  const cap = getOrderAmountCap(row.order);
  return cap === '' ? '' : allowOnlyWholePositiveNumbers(cap);
}

export function mapSplitRowsForSave(
  sourceOrder: MaturityOrder,
  splitRows: SplitOrderRow[],
): MaturityOrder[] {
  return splitRows.map((row, index) => {
    const quantity = resolveSplitRowQuantity(row);
    const lineNo = index + 1;
    return {
      ...sourceOrder,
      ...row.order,
      ORDER_ID: resolveSplitRowOrderId(sourceOrder, row, index),
      QUANTITY: quantity,
      IS_SPLIT: true,
      IS_SPLIT_MATURITY: false,
      HAS_SPLIT: false,
      LINE_NO: lineNo,
      SPLIT_LINE_NO: lineNo,
    };
  });
}

/** True when a split modal line may be edited or included in Save Split payloads. */
export function isSplitRowEditable(row: SplitOrderRow): boolean {
  if (row.rowType !== 'split') return false;
  return !isOrderInTransit(row.order);
}

/**
 * True when a split row has neither an order amount nor any conditions set.
 * These blank rows are excluded from the save payload when Save Split is triggered.
 */
export function isSplitRowBlank(row: SplitOrderRow): boolean {
  if (row.rowType !== 'split') return false;
  if (row.order.IS_ZERO_CANCEL_INTENT === true) return false;
  const rowOrder = { ...row.order, QUANTITY: row.splitAmount ?? row.order.QUANTITY };
  return !hasOrderAmountApplied(rowOrder) && !hasOrderConditions(rowOrder);
}

/** Maps only editable split lines for persistence; in-transit rows are skipped. */
export function getSplitRowsForSave(
  sourceOrder: MaturityOrder,
  splitRows: SplitOrderRow[],
): MaturityOrder[] {
  return mapSplitRowsForSave(sourceOrder, splitRows.filter(isSplitRowEditable));
}

const SUBMITTED_TRACK_STATUSES: ReadonlySet<OrderStatusEnum> = new Set([
  OrderStatusEnum.SUBMITTED,
  OrderStatusEnum.ACCEPTED,
  OrderStatusEnum.SUBMITTED_AND_DRAFT,
]);

/** True when a split line was ALL-cleared on the submitted track and needs IS_CLEARING_ALL_AMOUNTS on save. */
export function isSplitRowSubmittedTrackAllClear(row: SplitOrderRow): boolean {
  if (row.rowType !== 'split' || !isSplitRowEditable(row)) return false;
  if (row.order.IS_ZERO_CANCEL_INTENT === true) return false;
  if (allowOnlyWholePositiveNumbers(String(row.order.QUANTITY ?? '').trim()) === '0') return false;
  if (String(row.splitAmount ?? '').trim() !== '') return false;
  const orderForCap: MaturityOrder = { ...row.order, QUANTITY: row.splitAmount };
  if (isFullParOrderAmountApplied(orderForCap)) return false;
  if (!hasSubmittedAmountOnRecord(row.order)) return false;
  const status = row.order.ORDER_STATUS;
  return status != null && SUBMITTED_TRACK_STATUSES.has(status);
}

/** Builds a child order payload for persisting an ALL-cleared submitted-track split line. */
export function mapSplitRowForAllClearSave(
  sourceOrder: MaturityOrder,
  row: SplitOrderRow,
  lineIndex: number,
): MaturityOrder {
  const lineNo = lineIndex + 1;
  return {
    ...sourceOrder,
    ...row.order,
    ORDER_ID: resolveSplitRowOrderId(sourceOrder, row, lineIndex),
    QUANTITY: String(row.order.QUANTITY ?? '').trim(),
    IS_SPLIT: true,
    IS_SPLIT_MATURITY: false,
    HAS_SPLIT: false,
    LINE_NO: lineNo,
    SPLIT_LINE_NO: lineNo,
  };
}

/** Submitted-track split children cleared via ALL that must save with IS_CLEARING_ALL_AMOUNTS. */
export function getSplitRowsForAllClearSave(
  sourceOrder: MaturityOrder,
  splitRows: SplitOrderRow[],
): MaturityOrder[] {
  const editableSplitRows = splitRows.filter(isSplitRowEditable);
  return editableSplitRows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => isSplitRowSubmittedTrackAllClear(row))
    .map(({ row, index }) => mapSplitRowForAllClearSave(sourceOrder, row, index));
}

function isEditingExistingSplit(
  sourceOrder: MaturityOrder,
  baselineChildIds: Iterable<string>,
): boolean {
  if (isSplitMaturityGridOrder(sourceOrder)) {
    return true;
  }
  for (const orderId of baselineChildIds) {
    if (String(orderId ?? '').trim() !== '') {
      return true;
    }
  }
  return false;
}

export type SplitOrderRowValidationDiagnostic = {
  rowId: string;
  countsAsValid: boolean;
  reason: string;
};

function evaluateSplitRowSaveEligibility(
  row: SplitOrderRow,
  dealStatus: string | undefined,
): { countsAsValid: boolean; reason: string } {
  if (!isSplitRowEditable(row)) {
    const countsAsValid =
      hasOrderAmountApplied(row.order) || hasSubmittedAmountOnRecord(row.order);
    return {
      countsAsValid,
      reason: countsAsValid ? 'in-transit with amount' : 'in-transit without amount',
    };
  }
  const quantity = resolveSplitRowQuantity(row);
  if (!quantity) {
    return { countsAsValid: false, reason: 'no order amount' };
  }
  const validationOrder = getSplitRowValidationOrder(row);
  if (!validationOrder) {
    return { countsAsValid: false, reason: 'missing validation order' };
  }
  const orderForValidation: MaturityOrder = {
    ...validationOrder,
    QUANTITY: quantity,
    IS_ZERO_CANCEL_INTENT: row.order.IS_ZERO_CANCEL_INTENT,
  };
  if (!hasOrderAmountApplied(orderForValidation) && !orderForValidation.IS_ZERO_CANCEL_INTENT) {
    return { countsAsValid: false, reason: 'amount not positive' };
  }
  const validationState = getQuantityCellValidationState(orderForValidation, dealStatus);
  if (validationState.hasError) {
    return {
      countsAsValid: false,
      reason: validationState.tooltip ?? 'quantity validation error',
    };
  }
  return { countsAsValid: true, reason: 'valid' };
}

function getSplitOrderRowValidationDiagnostics(
  splitRows: SplitOrderRow[],
  dealStatus: string | undefined,
): SplitOrderRowValidationDiagnostic[] {
  return splitRows
    .filter((row) => row.rowType === 'split')
    .map((row) => ({ rowId: row.rowId, ...evaluateSplitRowSaveEligibility(row, dealStatus) }));
}

const MIN_SPLIT_VALID_ORDERS = 2;

export type SplitOrderSaveState = {
  validOrderCount: number;
  isEditingExisting: boolean;
  canSave: boolean;
  shouldCollapse: boolean;
  shouldPersistAsSplit: boolean;
  validationMessage: string | null;
  rowDiagnostics: SplitOrderRowValidationDiagnostic[];
};

/** Single source of truth for split modal save eligibility and validation messaging. */
export function getSplitOrderSaveState(
  sourceOrder: MaturityOrder,
  splitRows: SplitOrderRow[],
  baselineChildIds: Iterable<string>,
  dealStatus: string | undefined,
): SplitOrderSaveState {
  const rowDiagnostics = getSplitOrderRowValidationDiagnostics(splitRows, dealStatus);
  const validOrderCount = rowDiagnostics.filter((row) => row.countsAsValid).length;
  const isEditingExisting = isEditingExistingSplit(sourceOrder, baselineChildIds);
  const splitIsValid = validOrderCount >= MIN_SPLIT_VALID_ORDERS;
  const savableOrderCount = getSplitRowsForSave(sourceOrder, splitRows).length;
  const shouldCollapse =
    isEditingExisting && validOrderCount === 1 && savableOrderCount === 1;
  const canSave = splitIsValid || shouldCollapse;

  let validationMessage: string | null = null;
  if (!canSave) {
    validationMessage =
      validOrderCount === 1
        ? 'A split requires at least two orders with valid order amounts.'
        : 'Enter valid order amounts on at least two split lines.';
  }

  return {
    validOrderCount,
    isEditingExisting,
    canSave,
    shouldCollapse,
    shouldPersistAsSplit: splitIsValid,
    validationMessage,
    rowDiagnostics,
  };
}

/**
 * True when footer Remove Split may collapse an existing persisted single-child split.
 * Must match the server/parent guard (exactly one persisted child) so the click always
 * collapses; status-agnostic so DRAFT and ACCEPTED single-child splits behave the same.
 */
export function shouldShowRemoveSplit(
  splitRows: SplitOrderRow[],
  baselineChildIds: Iterable<string>,
): boolean {
  const baselineCount = [...baselineChildIds].filter((id) => String(id ?? '').trim() !== '').length;
  const splitRowCount = splitRows.filter((row) => row.rowType === 'split').length;
  return baselineCount === 1 && splitRowCount === 1;
}

/** True when the minus control may remove this split line (unsaved rows or persisted DRAFT only). */
export function isSplitRowRemovable(row: SplitOrderRow): boolean {
  if (row.rowType !== 'split') return false;
  const orderId = String(row.order.ORDER_ID ?? '').trim();
  if (!orderId) return true;
  return row.order.ORDER_STATUS === OrderStatusEnum.DRAFT;
}

export function childBondOrderToSplitRow(
  sourceOrder: MaturityOrder,
  child: MaturityOrder,
  rowId: string,
): SplitOrderRow {
  const merged = mergeChildWithGridParent(sourceOrder, child);
  const persistedZero =
    parseNumberAmount(String(child.QUANTITY ?? '').trim()) === 0 &&
    canSetZeroCancelOrderAmount(merged);
  const order = persistedZero ? { ...merged, IS_ZERO_CANCEL_INTENT: true } : merged;
  const splitAmount = formatOrderQuantityForCell(order);
  const acceptedOrderBaseline = buildAcceptedOrderBaseline(order) ?? undefined;
  return {
    rowId,
    rowType: 'split',
    splitAmount,
    acceptedOrderBaseline,
    order,
  };
}

const SPLIT_ROW_POLL_STATUS_FIELDS: (keyof MaturityOrder)[] = [
  'ORDER_STATUS',
  'ERROR_MESSAGE',
  'IS_CONFIRMED',
  'RECONFIRM_ELIGIBLE',
];

/** Stable signature for split-line status fields used by modal polling. */
export function getSplitRowsPollStatusSignature(rows: SplitOrderRow[]): string {
  return JSON.stringify(
    rows
      .filter((row) => row.rowType === 'split')
      .map((row) => ({
        orderId: row.order.ORDER_ID ?? '',
        ...Object.fromEntries(
          SPLIT_ROW_POLL_STATUS_FIELDS.map((field) => [field, row.order[field] ?? null]),
        ),
      })),
  );
}

/** True when any split modal line is PENDING or PENDING_CANCELLED. */
export function hasInTransitSplitRows(rows: SplitOrderRow[]): boolean {
  return rows.some((row) => row.rowType === 'split' && isOrderInTransit(row.order));
}

/** Merge server children into persisted split modal rows (all rows, or only orderIdsToSync). */
export function mergePersistedSplitRowsFromFetchedChildren(
  sourceOrder: MaturityOrder,
  existingRows: SplitOrderRow[],
  fetchedChildren: MaturityOrder[],
  orderIdsToSync?: Iterable<string>,
): SplitOrderRow[] {
  const syncOrderIds = orderIdsToSync
    ? new Set(
        [...orderIdsToSync]
          .map((orderId) => String(orderId ?? '').trim())
          .filter(Boolean),
      )
    : null;
  const childrenByOrderId = new Map(
    fetchedChildren
      .filter((child) => String(child.ORDER_ID ?? '').trim() !== '')
      .map((child) => [String(child.ORDER_ID), child]),
  );

  return existingRows.map((row) => {
    if (row.rowType !== 'split') {
      return row;
    }
    const orderId = String(row.order.ORDER_ID ?? '').trim();
    if (!orderId) {
      return row;
    }
    if (syncOrderIds && !syncOrderIds.has(orderId)) {
      return row;
    }
    const serverChild = childrenByOrderId.get(orderId);
    if (!serverChild) {
      return row;
    }
    return childBondOrderToSplitRow(sourceOrder, serverChild, row.rowId);
  });
}

/** Merge server children into modal rows for in-transit lines only; other rows are left unchanged. */
export function mergeInTransitSplitRowsFromFetchedChildren(
  sourceOrder: MaturityOrder,
  existingRows: SplitOrderRow[],
  fetchedChildren: MaturityOrder[],
): { rows: SplitOrderRow[]; changed: boolean; mergedOrderIds: string[] } {
  const childrenByOrderId = new Map(
    fetchedChildren
      .filter((child) => String(child.ORDER_ID ?? '').trim() !== '')
      .map((child) => [String(child.ORDER_ID), child]),
  );
  const beforeSignature = getSplitRowsPollStatusSignature(existingRows);
  const mergedOrderIds: string[] = [];

  const nextRows = existingRows.map((row) => {
    if (row.rowType !== 'split') {
      return row;
    }

    const orderId = String(row.order.ORDER_ID ?? '').trim();
    if (!orderId) {
      return row;
    }

    const serverChild = childrenByOrderId.get(orderId);
    if (!serverChild) {
      return row;
    }

    if (!isOrderInTransit(row.order) && !isOrderInTransit(serverChild)) {
      return row;
    }

    mergedOrderIds.push(orderId);
    return childBondOrderToSplitRow(sourceOrder, serverChild, row.rowId);
  });

  return {
    rows: nextRows,
    changed: getSplitRowsPollStatusSignature(nextRows) !== beforeSignature,
    mergedOrderIds,
  };
}

export type ApplyFetchedChildrenOptions = {
  nextSplitRowId?: { value: number };
  /** When true, replace persisted split rows from the server response (e.g. after reset). */
  syncPersistedFromServer?: boolean;
  /** When set with syncPersistedFromServer, only those ORDER_IDs are replaced; other rows keep local edits. */
  syncPersistedOrderIds?: Iterable<string>;
};

/** Merges server children into modal rows and syncs row list after deletes/new server lines. */
export function applyFetchedChildrenToSplitRows(
  sourceOrder: MaturityOrder,
  existingRows: SplitOrderRow[],
  fetchedChildren: MaturityOrder[],
  options?: ApplyFetchedChildrenOptions,
): SplitOrderRow[] {
  const sourceRow: SplitOrderRow =
    existingRows.find((row) => row.rowType === 'source') ?? createSourceSplitOrderRow(sourceOrder);

  if (fetchedChildren.length === 0) {
    const unsavedSplitRows = existingRows.filter(
      (row) => row.rowType === 'split' && !String(row.order.ORDER_ID ?? '').trim(),
    );
    return [sourceRow, ...unsavedSplitRows];
  }

  const mergedRows = options?.syncPersistedFromServer
    ? mergePersistedSplitRowsFromFetchedChildren(
        sourceOrder,
        existingRows,
        fetchedChildren,
        options.syncPersistedOrderIds,
      )
    : mergeInTransitSplitRowsFromFetchedChildren(sourceOrder, existingRows, fetchedChildren).rows;
  const serverOrderIds = new Set(
    fetchedChildren
      .map((child) => String(child.ORDER_ID ?? '').trim())
      .filter(Boolean),
  );

  const splitRows = mergedRows.filter((row) => {
    if (row.rowType !== 'split') return false;
    const orderId = String(row.order.ORDER_ID ?? '').trim();
    if (!orderId) return true;
    return serverOrderIds.has(orderId);
  });

  const existingOrderIds = new Set(
    splitRows
      .map((row) => String(row.order.ORDER_ID ?? '').trim())
      .filter(Boolean),
  );
  let nextSplitRowId = options?.nextSplitRowId?.value ?? 0;
  for (const child of fetchedChildren) {
    const orderId = String(child.ORDER_ID ?? '').trim();
    if (!orderId || existingOrderIds.has(orderId)) continue;
    nextSplitRowId += 1;
    splitRows.push(childBondOrderToSplitRow(sourceOrder, child, `split-${nextSplitRowId}`));
    existingOrderIds.add(orderId);
  }
  if (options?.nextSplitRowId) {
    options.nextSplitRowId.value = nextSplitRowId;
  }

  return [sourceRow, ...splitRows];
}

/** Sums entered split-line amounts (empty lines contribute 0). */
export function sumSplitOrderAmounts(splitRows: SplitOrderRow[]): number {
  return splitRows
    .filter((row) => row.rowType === 'split')
    .reduce((total, row) => total + (parseNumberAmount(row.splitAmount) ?? 0), 0);
}

function hasAnySplitLineAmount(splitRows: SplitOrderRow[]): boolean {
  return splitRows.some(
    (row) => row.rowType === 'split' && String(row.splitAmount ?? '').trim() !== '',
  );
}

/** Raw integer string total for parent QUANTITY storage (sum of split-line amounts). */
export function getSplitParentQuantityTotal(splitRows: SplitOrderRow[]): string {
  if (!hasAnySplitLineAmount(splitRows)) {
    return '';
  }
  return String(sumSplitOrderAmounts(splitRows));
}

/** Formatted total for the split modal parent amount cell; blank when no line has an amount. */
export function formatSplitOrderAmountTotal(splitRows: SplitOrderRow[]): string {
  const total = getSplitParentQuantityTotal(splitRows);
  return total === '' ? '' : orderAmountFormatter(total);
}

/** True when every split child line is at full cap (principal for zero coupons, par otherwise). */
export function areAllSplitChildrenAtFullCap(
  sourceOrder: MaturityOrder,
  splitRows: SplitOrderRow[],
): boolean {
  const children = splitRows.filter((row) => row.rowType === 'split');
  if (!children.length) return false;
  return children.every((row) =>
    isFullParOrderAmountApplied({ ...sourceOrder, ...row.order, QUANTITY: row.splitAmount }),
  );
}

export type ClearAllDraftBuckets = {
  resetOrders: MaturityOrder[];
  deleteOrders: MaturityOrder[];
  splitParents: MaturityOrder[];
};

/**
 * Resolves grid rows into reset/delete targets for global Clear All Draft Orders.
 * Split-maturity parents are expanded to their persisted child lines (LINE_NO > 0); rollup
 * anchors are never sent to the backend.
 */
export async function resolveClearAllDraftBuckets(
  connect: Connect,
  gridOrders: MaturityOrder[],
  options: { isInRepriceWindow: boolean; dealStatus?: string },
): Promise<ClearAllDraftBuckets> {
  const resetOrders: MaturityOrder[] = [];
  const deleteOrders: MaturityOrder[] = [];
  const splitParents: MaturityOrder[] = [];

  const addDraftLine = (order: MaturityOrder, splitParent?: MaturityOrder): void => {
    if (isOrderInTransit(order)) return;
    if (options.isInRepriceWindow) {
      if (isReconfirmResetTarget(order, options.dealStatus, splitParent)) {
        resetOrders.push(order);
      }
      return;
    }
    if (order.ORDER_STATUS === OrderStatusEnum.SUBMITTED_AND_DRAFT) {
      resetOrders.push(order);
    } else if (order.ORDER_STATUS === OrderStatusEnum.DRAFT) {
      deleteOrders.push(order);
    }
  };

  const splitParentOrders = gridOrders.filter(isSplitMaturityGridOrder);
  const otherOrders = gridOrders.filter((order) => !isSplitMaturityGridOrder(order));

  const childrenByParent = await Promise.all(
    splitParentOrders.map(async (parent) => ({
      parent,
      children: await fetchBondOrderChildren(connect, parent.MATURITY_ID),
    })),
  );

  for (const { parent, children } of childrenByParent) {
    splitParents.push(parent);
    for (const child of children) {
      addDraftLine(mergeChildWithGridParent(parent, child), parent);
    }
  }

  for (const order of otherOrders) {
    if (isDraftOrder(order)) {
      addDraftLine(order);
      continue;
    }

    if (options.isInRepriceWindow && isReconfirmResetTarget(order, options.dealStatus)) {
      resetOrders.push(order);
    }
  }

  return { resetOrders, deleteOrders, splitParents };
}

/** Grid row after all split child lines are removed — maturity placeholder with no persisted order. */
export function buildGridRowAfterSplitChildrenCleared(parent: MaturityOrder): MaturityOrder {
  const cleared: MaturityOrder = {
    ...parent,
    ORDER_ID: '',
    ORDER_STATUS: undefined,
    RECONFIRM_ELIGIBLE: undefined,
    HAS_SPLIT: false,
    IS_SPLIT_MATURITY: false,
    IS_SPLIT: false,
    SPLIT_CONDITIONS_VARIOUS: false,
    LINE_NO: undefined,
    SPLIT_LINE_NO: undefined,
    QUANTITY: '',
    SUBMITTED_QUANTITY: undefined,
    IS_AUTO_RESET_ORDER_AMOUNT: false,
    IS_ZERO_CANCEL_INTENT: false,
    IS_CONFIRMED: undefined,
    ERROR_MESSAGE: undefined,
    MINIMUM_ORDER_AMOUNT: undefined,
    MAXIMUM_ORDER_AMOUNT: undefined,
    ORDER_AMOUNT_INCREMENT: undefined,
  };
  clearSplitConditionFields(cleared);
  return cleared;
}

/** True when the grid anchor row should be replaced with a child-derived rollup. */
export function splitParentRollupNeedsRefresh(
  current: MaturityOrder,
  rollup: MaturityOrder,
): boolean {
  if (String(current.QUANTITY ?? '').trim() !== String(rollup.QUANTITY ?? '').trim()) {
    return true;
  }
  if (!!current.IS_AUTO_RESET_ORDER_AMOUNT !== !!rollup.IS_AUTO_RESET_ORDER_AMOUNT) {
    return true;
  }
  if (current.ORDER_STATUS !== rollup.ORDER_STATUS) {
    return true;
  }
  if (!!current.HAS_SPLIT !== !!rollup.HAS_SPLIT) {
    return true;
  }
  if (!!current.IS_SPLIT_MATURITY !== !!rollup.IS_SPLIT_MATURITY) {
    return true;
  }
  return !!current.SPLIT_CONDITIONS_VARIOUS !== !!rollup.SPLIT_CONDITIONS_VARIOUS;
}

export type ResolveSplitParentRollupsResult = {
  updates: MaturityOrder[];
  hasInTransitChildren: boolean;
};

/**
 * Recomputes split-maturity parent rows from persisted child lines. Used when ALL_MATURITY_ORDERS
 * streams anchor QUANTITY that may be stale relative to the sum of split children.
 */
export async function resolveSplitParentRollupsForGrid(
  connect: Connect,
  gridOrders: MaturityOrder[],
  options?: { parentsOnly?: MaturityOrder[] },
): Promise<ResolveSplitParentRollupsResult> {
  const updates: MaturityOrder[] = [];
  let hasInTransitChildren = false;
  const parents = options?.parentsOnly ?? gridOrders.filter(isSplitMaturityGridOrder);

  for (const parent of parents) {
    if (!isSplitMaturityGridOrder(parent)) continue;
    const children = await fetchBondOrderChildren(connect, parent.MATURITY_ID);
    if (children.some(isOrderInTransit)) {
      hasInTransitChildren = true;
    }
    const rollup = buildSplitParentRollupFromChildren(
      parent,
      children.map((child) => mergeChildWithGridParent(parent, child)),
    );
    if (splitParentRollupNeedsRefresh(parent, rollup)) {
      updates.push(rollup);
    }
  }
  return { updates, hasInTransitChildren };
}

/** Derives the split-maturity grid rollup row from persisted child order lines. */
export function buildSplitParentRollupFromChildren(
  parent: MaturityOrder,
  children: MaturityOrder[],
): MaturityOrder {
  if (children.length === 0) {
    return buildGridRowAfterSplitChildrenCleared(parent);
  }

  const splitRows = children.map((child, index) =>
    childBondOrderToSplitRow(parent, child, `rollup-${index}`),
  );
  return {
    ...parent,
    ...getSplitConditionsRollupOrder(parent, splitRows),
    HAS_SPLIT: children.length > 0,
    IS_SPLIT_MATURITY: children.length > 0,
    IS_SPLIT: false,
    QUANTITY: getSplitParentQuantityTotal(splitRows),
    IS_AUTO_RESET_ORDER_AMOUNT: false,
    IS_ZERO_CANCEL_INTENT: false,
  };
}
